import React, { useState, useEffect } from 'react';
import { useAuth0 } from "@auth0/auth0-react";
import './styles.css';

export default function AddMemberModal({ familyId, usage, onClose, onSuccess, onOpen }) {
  const { getAccessTokenSilently } = useAuth0();
  const [memberName, setMemberName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [memberIsAdmin, setMemberIsAdmin] = useState(false);
  const [isOver13, setIsOver13] = useState(true);
  const [parentalConsentVerified, setParentalConsentVerified] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

  // Call onOpen when component mounts to refresh usage data
  useEffect(() => {
    if (onOpen) {
      onOpen();
    }
  }, [onOpen]);

  // Check if member limit is reached (handle Unlimited plans and null/undefined)
  const isMemberLimitReached = usage &&
    usage.memberLimit != null &&  // Check for null AND undefined
    usage.memberLimit !== Infinity &&
    usage.memberLimit < 999999 &&
    usage.memberCount >= usage.memberLimit;

  const isAdminLimitReached = memberIsAdmin && usage &&
    usage.adminLimit != null &&  // Check for null AND undefined
    usage.adminLimit !== Infinity &&
    usage.adminLimit < 999999 &&
    usage.adminCount >= usage.adminLimit;

  const handleAddMember = async (e) => {
    e.preventDefault();
    setError('');
    if (!memberName || !memberEmail) {
      setError('Name and email are required.');
      return;
    }

    // Validate parental consent for children under 13
    if (!isOver13 && !parentalConsentVerified) {
      setError('Parental consent verification is required for children under 13.');
      return;
    }
    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: "https://api.eternavaults.com"
      });
      // Generate a synthetic auth0Id for manual members
      const syntheticAuth0Id = `manual-${memberEmail}-${Date.now()}`;
      const res = await fetch(`${API_BASE_URL}/api/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: memberName,
          email: memberEmail,
          familyId,
          isAdmin: memberIsAdmin,
          auth0Id: syntheticAuth0Id,
          isOver13: isOver13,
          parentalConsentVerified: isOver13 ? true : parentalConsentVerified
        })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to add member');
      }

      const successData = await res.json();
      setSuccessData(successData);

      if (onSuccess) onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const closeSuccess = () => {
    setSuccessData(null);
    if (onClose) onClose();
  };

  // Success Modal
  if (successData) {
    return (
      <div className="modal-overlay">
        <div className="modal-content" style={{ maxWidth: 600 }}>
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <h2 style={{ color: '#4caf50', marginBottom: '1rem' }}>✅ Member Added Successfully!</h2>

            <div style={{
              background: '#18120b',
              border: '2px solid #4caf50',
              borderRadius: '12px',
              padding: '1.5rem',
              margin: '1.5rem 0',
              textAlign: 'left'
            }}>
              <h3 style={{ color: '#4caf50', marginBottom: '1rem' }}>📧 Member Details</h3>
              <p style={{ color: '#e2c89c', marginBottom: '0.5rem' }}>
                <strong>Member:</strong> {successData.name}
              </p>
              <p style={{ color: '#e2c89c', marginBottom: '1rem' }}>
                <strong>Email:</strong> {successData.memberEmail}
              </p>
              {!isOver13 && (
                <p style={{ color: '#d4af37', marginBottom: '1rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                  📧 Child account credentials sent to your email for your records
                </p>
              )}
            </div>

            <div style={{
              background: '#e8f5e8',
              border: '2px solid #4caf50',
              borderRadius: '8px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              <h4 style={{ color: '#2e7d32', marginBottom: '1rem' }}>📧 Email Sent!</h4>
              <p style={{ color: '#2e7d32', margin: 0, fontSize: '1.1rem' }}>
                <strong>{successData.name}</strong> should have received an email with their temporary access code and instructions to get started.
              </p>
              <p style={{ color: '#666', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>
                If they don't see the email, please ask them to check their spam folder.
              </p>
            </div>

            <button
              onClick={closeSuccess}
              style={{
                background: '#4caf50',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '0.8rem 2rem',
                cursor: 'pointer',
                fontSize: '1rem'
              }}
            >
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Add Family Member</h2>

        {/* Usage info */}
        {usage && (
          <div style={{
            background: '#18120b',
            border: '2px solid #bfa97a',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#e2c89c',
            fontFamily: 'Cinzel, serif',
            fontSize: '0.95rem'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <strong>Current Usage:</strong> {usage.memberCount} / {usage.memberLimit == null || usage.memberLimit >= 999999 ? 'Unlimited' : usage.memberLimit} members
            </div>
            {usage.adminLimit && (
              <div>
                <strong>Admins:</strong> {usage.adminCount} / {usage.adminLimit == null || usage.adminLimit >= 999999 ? 'Unlimited' : usage.adminLimit}
              </div>
            )}
          </div>
        )}

        <form className="luxury-form" onSubmit={handleAddMember}>
          <div className="form-group">
            <label>Name</label>
            <input type="text" value={memberName} onChange={e => setMemberName(e.target.value)} required disabled={loading || isMemberLimitReached} />
          </div>
          <div className="form-group">
            <label>Email Address</label>
            <input type="email" value={memberEmail} onChange={e => setMemberEmail(e.target.value)} required disabled={loading || isMemberLimitReached} placeholder="Enter email for passcode reset" />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={isOver13}
                onChange={e => {
                  setIsOver13(e.target.checked);
                  if (e.target.checked) {
                    setParentalConsentVerified(false);
                  }
                }}
                disabled={loading || isMemberLimitReached}
              />
              This member is over 13 years old
            </label>
            {!isOver13 && (
              <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: '#2a1f0f', border: '1px solid #e2c89c', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2c89c' }}>
                  <input
                    type="checkbox"
                    checked={parentalConsentVerified}
                    onChange={e => setParentalConsentVerified(e.target.checked)}
                    disabled={loading || isMemberLimitReached}
                  />
                  I verify that I have parental consent for this child under 13
                </label>
                <p style={{ fontSize: '0.85rem', color: '#d4af37', marginTop: '0.5rem', marginBottom: 0 }}>
                  ⚠️ Children under 13 will not be able to reset their own passcode and require admin assistance.
                </p>
                <p style={{ fontSize: '0.85rem', color: '#bfa97a', marginTop: '0.5rem', marginBottom: 0, fontStyle: 'italic' }}>
                  📧 The child's access credentials (including privacy passcode) will be sent to your email address for your records.
                </p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={memberIsAdmin}
                onChange={e => setMemberIsAdmin(e.target.checked)}
                disabled={loading || isMemberLimitReached || (!memberIsAdmin && usage && usage.adminCount >= usage.adminLimit)}
              />
              Make this member an admin
              {!memberIsAdmin && usage && usage.adminCount >= usage.adminLimit && (
                <span style={{
                  color: '#e2c89c',
                  fontSize: '0.9rem',
                  marginLeft: '0.5rem',
                  fontStyle: 'italic'
                }}>
                  (Admin limit reached)
                </span>
              )}
            </label>
          </div>

          {/* Limit reached message */}
          {isMemberLimitReached && (
            <div style={{
              background: '#18120b',
              border: '2px solid #e2c89c',
              color: '#e2c89c',
              fontFamily: 'Cinzel, serif',
              fontWeight: 600,
              fontSize: '1rem',
              borderRadius: '10px',
              padding: '1rem',
              marginBottom: '1.5rem',
              textAlign: 'center'
            }}>
              You have reached the member limit for your plan. Please upgrade your plan to add more members.
            </div>
          )}


          {error && (
            <div className="form-error" style={{
              background: error.includes('limit') ? '#18120b' : '#fbeee6',
              border: error.includes('limit') ? '2px solid #e2c89c' : '2px solid #f44336',
              color: error.includes('limit') ? '#e2c89c' : '#f44336',
              fontFamily: 'Cinzel, serif',
              fontWeight: 600,
              fontSize: '1.08rem',
              borderRadius: '10px',
              padding: '1rem 2rem',
              marginBottom: '1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: error.includes('limit') ? '0 2px 12px #bfa97a22' : '0 2px 12px #f4433622',
            }}>
              <span>{error.includes('Member limit') ? 'You have reached the member limit for your plan. Please upgrade your plan to add more members.' : error.includes('Admin limit') ? 'You have reached the admin limit for your plan. Please upgrade your plan to add more admins.' : error}</span>
              <button
                onClick={() => setError("")}
                style={{
                  background: 'none',
                  border: 'none',
                  color: error.includes('limit') ? '#e2c89c' : '#f44336',
                  fontSize: '1.3rem',
                  cursor: 'pointer',
                  marginLeft: '2rem',
                }}
                aria-label="Close error message"
              >
                ×
              </button>
            </div>
          )}
          <button
            type="submit"
            className="form-submit"
            disabled={loading || isMemberLimitReached}
            style={{
              marginTop: '1rem',
              opacity: isMemberLimitReached ? 0.5 : 1,
              cursor: isMemberLimitReached ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Adding...' : isMemberLimitReached ? 'Member Limit Reached' : 'Add Member'}
          </button>
        </form>
      </div>
    </div>
  );
}
